import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useAuth } from "../App";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  MessageSquare, Trash2, Clock, CheckCircle2, XCircle, Loader2, RefreshCw, Lock, Eye, History, AlertTriangle,
} from "lucide-react";

// ============ Config visual de estados ============
const ESTADOS_META = {
  abierto:     { label: "Abierto",     color: "bg-orange-100 text-orange-800 border-orange-200", icon: Clock, dot: "bg-orange-500" },
  en_revision: { label: "En revisión", color: "bg-blue-100 text-blue-800 border-blue-200",       icon: Eye, dot: "bg-blue-500" },
  en_proceso:  { label: "En proceso",  color: "bg-purple-100 text-purple-800 border-purple-200", icon: Loader2, dot: "bg-purple-500" },
  resuelto:    { label: "Resuelto",    color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: CheckCircle2, dot: "bg-emerald-500" },
  cerrado:     { label: "Cerrado",     color: "bg-slate-200 text-slate-700 border-slate-300",    icon: Lock, dot: "bg-slate-500" },
  rechazado:   { label: "Rechazado",   color: "bg-rose-100 text-rose-800 border-rose-200",       icon: XCircle, dot: "bg-rose-500" },
  reabierto:   { label: "Reabierto",   color: "bg-amber-100 text-amber-800 border-amber-200",    icon: RefreshCw, dot: "bg-amber-500" },
};

const estadoLabel = (v) => ESTADOS_META[v]?.label || v || "-";

function EstadoBadge({ estado, size = "sm" }) {
  const meta = ESTADOS_META[estado] || ESTADOS_META.abierto;
  const Icon = meta.icon;
  return (
    <Badge className={`${meta.color} border ${size === "lg" ? "text-sm py-1 px-3" : ""} hover:opacity-100`}>
      <Icon className={`${size === "lg" ? "w-4 h-4" : "w-3 h-3"} mr-1`} />
      {meta.label}
    </Badge>
  );
}

// ============ Componente principal ============
export default function ReclamosPage() {
  const { token, API } = useAuth();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [transiciones, setTransiciones] = useState({});
  const [autoCierreDias, setAutoCierreDias] = useState(7);

  // Modal cambio de estado
  const [showEstadoDialog, setShowEstadoDialog] = useState(false);
  const [nuevoEstado, setNuevoEstado] = useState("");
  const [comentario, setComentario] = useState("");
  const [motivo, setMotivo] = useState("");

  // Respuesta oficial
  const [respuesta, setRespuesta] = useState("");

  const load = async () => {
    try {
      const params = filter === "all" ? {} : { estado: filter };
      const r = await axios.get(`${API}/reclamos`, { ...auth, params });
      setItems(r.data);
    } catch (e) { toast.error("Error cargando reclamos"); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => {
    axios.get(`${API}/reclamos/estados`, auth)
      .then(r => { setTransiciones(r.data.transiciones || {}); setAutoCierreDias(r.data.auto_cierre_dias || 7); })
      .catch(() => {});
  }, [auth]);

  // Detalle actualizado en tiempo real (al refrescar lista)
  useEffect(() => {
    if (!detail) return;
    const updated = items.find(x => x.id === detail.id);
    if (updated) setDetail(updated);
  // eslint-disable-next-line
  }, [items]);

  const openDetail = (r) => {
    setDetail(r);
    setRespuesta(r.respuesta || "");
  };

  const abrirCambioEstado = () => {
    setNuevoEstado("");
    setComentario("");
    setMotivo("");
    setShowEstadoDialog(true);
  };

  const guardarCambioEstado = async () => {
    if (!detail || !nuevoEstado) { toast.error("Seleccione un estado"); return; }
    if (nuevoEstado === "rechazado" && !motivo.trim()) {
      toast.error("El motivo es obligatorio al rechazar");
      return;
    }
    try {
      const body = { nuevo_estado: nuevoEstado, comentario, motivo };
      const r = await axios.post(`${API}/reclamos/${detail.id}/cambiar-estado`, body, auth);
      toast.success(`Estado: ${ESTADOS_META[nuevoEstado]?.label || nuevoEstado}`);
      setShowEstadoDialog(false);
      await load();
      const upd = { ...detail, estado: r.data.estado, historial_estados: [...(detail.historial_estados || []), r.data.entrada] };
      setDetail(upd);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cambiar el estado");
    }
  };

  const guardarRespuesta = async () => {
    if (!detail) return;
    try {
      await axios.post(`${API}/reclamos/${detail.id}/responder`, { respuesta, estado: detail.estado }, auth);
      toast.success("Respuesta guardada");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error al guardar"); }
  };

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar reclamo? Esta acción no se puede deshacer.")) return;
    try {
      await axios.delete(`${API}/reclamos/${id}`, auth);
      toast.success("Reclamo eliminado");
      if (detail?.id === id) setDetail(null);
      load();
    } catch (e) { toast.error("Error al eliminar"); }
  };

  // Conteos por estado para las KPI chips
  const counts = useMemo(() => {
    const c = { total: items.length };
    for (const est of Object.keys(ESTADOS_META)) c[est] = 0;
    for (const r of items) c[r.estado || "abierto"] = (c[r.estado || "abierto"] || 0) + 1;
    return c;
  }, [items]);

  const proximosEstados = useMemo(() => {
    if (!detail) return [];
    return transiciones[detail.estado || "abierto"] || [];
  }, [detail, transiciones]);

  return (
    <div className="space-y-6" data-testid="reclamos-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-3xl text-green-900 flex items-center gap-3">
            <MessageSquare className="w-8 h-8 text-orange-500" />Reclamos
          </h1>
          <p className="text-green-700/70 mt-1">
            Atención de reclamos con flujo de trabajo y auditoría. Auto-cierre tras {autoCierreDias} días como &quot;Resuelto&quot;.
          </p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-56" data-testid="filter-reclamos"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos ({counts.total})</SelectItem>
            {Object.keys(ESTADOS_META).map(k => (
              <SelectItem key={k} value={k}>{ESTADOS_META[k].label} ({counts[k] || 0})</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI chips por estado */}
      <div className="flex flex-wrap gap-2">
        {Object.entries(ESTADOS_META).map(([k, m]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold transition ${filter === k ? "ring-2 ring-orange-400" : ""} ${m.color}`}
            data-testid={`chip-estado-${k}`}
          >
            <span className={`w-2 h-2 rounded-full ${m.dot}`} />
            {m.label} · <span className="font-mono">{counts[k] || 0}</span>
          </button>
        ))}
      </div>

      <Card className="p-4 border-green-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-green-50 text-green-900">
              <tr>
                <th className="text-left p-2.5">Ticket</th>
                <th className="text-left p-2.5">Fecha</th>
                <th className="text-left p-2.5">Tipo</th>
                <th className="text-left p-2.5">Asunto</th>
                <th className="text-left p-2.5">Contribuyente</th>
                <th className="text-center p-2.5">Estado</th>
                <th className="text-right p-2.5">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map(r => (
                <tr
                  key={r.id}
                  className="border-b border-green-50 hover:bg-green-50/40 cursor-pointer"
                  onClick={() => openDetail(r)}
                  data-testid={`row-reclamo-${r.nro_ticket}`}
                >
                  <td className="p-2.5 font-mono">{r.nro_ticket}</td>
                  <td className="p-2.5 text-xs">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="p-2.5 capitalize">{r.tipo}</td>
                  <td className="p-2.5 font-medium">{r.asunto}</td>
                  <td className="p-2.5 text-xs">{r.contribuyente_nombre || "(anónimo)"}</td>
                  <td className="p-2.5 text-center"><EstadoBadge estado={r.estado || "abierto"} /></td>
                  <td className="p-2.5 text-right" onClick={e => e.stopPropagation()}>
                    <Button size="icon" variant="ghost" onClick={() => eliminar(r.id)} data-testid={`btn-del-${r.nro_ticket}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
              {!items.length && (
                <tr><td colSpan="7" className="p-8 text-center text-slate-400">Sin reclamos</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detalle del reclamo */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <span>Reclamo {detail?.nro_ticket}</span>
              {detail && <EstadoBadge estado={detail.estado || "abierto"} size="lg" />}
            </DialogTitle>
            <DialogDescription>
              Gestión y auditoría del ciclo de vida del reclamo.
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><span className="text-slate-500 text-xs uppercase tracking-wider">Asunto</span><p className="font-semibold text-green-900">{detail.asunto}</p></div>
                <div><span className="text-slate-500 text-xs uppercase tracking-wider">Tipo</span><p className="capitalize">{detail.tipo}</p></div>
                <div><span className="text-slate-500 text-xs uppercase tracking-wider">Fecha creación</span><p>{(detail.created_at || "").slice(0, 16).replace("T", " ")}</p></div>
                <div><span className="text-slate-500 text-xs uppercase tracking-wider">Dirección</span><p>{detail.direccion || "-"}</p></div>
                {detail.contribuyente_nombre && (
                  <div className="sm:col-span-2"><span className="text-slate-500 text-xs uppercase tracking-wider">Contribuyente</span><p>{detail.contribuyente_nombre} · {detail.contribuyente_documento}</p></div>
                )}
              </div>

              <div className="p-3 bg-green-50/60 rounded-lg border border-green-100">
                <p className="font-semibold text-green-900 mb-1 text-xs uppercase tracking-wider">Descripción del contribuyente</p>
                <p className="whitespace-pre-wrap text-slate-800">{detail.descripcion}</p>
              </div>

              {detail.motivo_rechazo && (
                <div className="p-3 bg-rose-50 rounded-lg border border-rose-200 flex gap-2 items-start" data-testid="motivo-rechazo-block">
                  <AlertTriangle className="w-4 h-4 mt-0.5 text-rose-700 shrink-0" />
                  <div>
                    <p className="font-semibold text-rose-900 text-xs uppercase tracking-wider">Motivo de rechazo</p>
                    <p className="text-rose-900">{detail.motivo_rechazo}</p>
                  </div>
                </div>
              )}

              {/* Botones de transición */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-green-100">
                <p className="w-full text-xs font-bold uppercase tracking-wider text-slate-500">Cambiar estado a:</p>
                {proximosEstados.length === 0 && <p className="text-xs text-slate-500 italic">Sin transiciones disponibles desde este estado.</p>}
                {proximosEstados.map(est => {
                  const meta = ESTADOS_META[est];
                  const Icon = meta.icon;
                  return (
                    <Button
                      key={est}
                      onClick={() => { setNuevoEstado(est); setComentario(""); setMotivo(""); setShowEstadoDialog(true); }}
                      variant="outline"
                      size="sm"
                      className={`border-2 ${meta.color} hover:brightness-95`}
                      data-testid={`btn-transicion-${est}`}
                    >
                      <Icon className="w-4 h-4 mr-1" /> {meta.label}
                    </Button>
                  );
                })}
              </div>

              {/* Respuesta oficial */}
              <div className="pt-2 border-t border-green-100">
                <p className="font-semibold text-green-900 text-xs uppercase tracking-wider mb-2">Respuesta oficial al contribuyente</p>
                <Textarea data-testid="textarea-respuesta" value={respuesta} onChange={e => setRespuesta(e.target.value)} rows={3} placeholder="Escriba la respuesta que verá el contribuyente..." />
                <div className="flex justify-end mt-2">
                  <Button size="sm" onClick={guardarRespuesta} className="bg-green-700 hover:bg-green-800 text-white" data-testid="btn-guardar-respuesta">
                    Guardar respuesta
                  </Button>
                </div>
              </div>

              {/* Historial (timeline) */}
              <div className="pt-3 border-t border-green-100">
                <p className="font-semibold text-green-900 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                  <History className="w-4 h-4" /> Historial de estados
                </p>
                <ol className="relative border-l-2 border-green-200 ml-2 space-y-3" data-testid="historial-timeline">
                  {(detail.historial_estados || []).slice().reverse().map((h, i) => {
                    const meta = ESTADOS_META[h.estado] || ESTADOS_META.abierto;
                    return (
                      <li key={i} className="pl-4 relative">
                        <span className={`absolute -left-[7px] top-1.5 w-3 h-3 rounded-full ${meta.dot} ring-2 ring-white`} />
                        <div className="flex flex-wrap items-baseline gap-2">
                          <EstadoBadge estado={h.estado} />
                          <span className="text-xs text-slate-500">
                            {(h.timestamp || "").slice(0, 16).replace("T", " ")} · por {h.usuario_nombre || "—"}
                            {h.usuario_rol && h.usuario_rol !== "sistema" && <span className="text-slate-400"> ({h.usuario_rol})</span>}
                          </span>
                        </div>
                        {h.comentario && <p className="text-slate-700 text-sm mt-1 whitespace-pre-wrap">{h.comentario}</p>}
                        {h.motivo && <p className="text-rose-700 text-xs mt-1"><strong>Motivo:</strong> {h.motivo}</p>}
                      </li>
                    );
                  })}
                  {!(detail.historial_estados || []).length && (
                    <li className="text-xs text-slate-400 italic pl-4">Sin registros históricos.</li>
                  )}
                </ol>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal cambio de estado */}
      <Dialog open={showEstadoDialog} onOpenChange={setShowEstadoDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Cambiar estado del reclamo</DialogTitle>
            <DialogDescription>
              {detail && (<>De <strong>{estadoLabel(detail.estado)}</strong> a <strong>{estadoLabel(nuevoEstado)}</strong></>)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Nuevo estado</label>
              <Select value={nuevoEstado} onValueChange={setNuevoEstado}>
                <SelectTrigger data-testid="select-nuevo-estado"><SelectValue placeholder="Seleccione..." /></SelectTrigger>
                <SelectContent>
                  {proximosEstados.map(est => (
                    <SelectItem key={est} value={est}>{estadoLabel(est)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {nuevoEstado === "rechazado" && (
              <div>
                <label className="text-xs font-bold uppercase tracking-wider text-rose-700">Motivo del rechazo *</label>
                <Textarea
                  value={motivo}
                  onChange={e => setMotivo(e.target.value)}
                  placeholder="Justifique por qué el reclamo no procede..."
                  rows={3}
                  data-testid="textarea-motivo-rechazo"
                />
                <p className="text-xs text-rose-700 mt-1">Este texto quedará registrado en el historial y será visible para el contribuyente.</p>
              </div>
            )}

            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Comentario (opcional)</label>
              <Textarea
                value={comentario}
                onChange={e => setComentario(e.target.value)}
                placeholder="Comentario interno o para el contribuyente..."
                rows={2}
                data-testid="textarea-comentario-estado"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowEstadoDialog(false)} data-testid="btn-cancelar-estado">Cancelar</Button>
            <Button
              onClick={guardarCambioEstado}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              disabled={!nuevoEstado || (nuevoEstado === "rechazado" && !motivo.trim())}
              data-testid="btn-confirmar-estado"
            >
              Confirmar cambio
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
